import React, { useState } from 'react';
import aiAutomationImg from '../assets/pexels-pavel-danilyuk-8438926.jpg';

const AIAutomation = () => {
  const [activeDemo, setActiveDemo] = useState(0);

  const testimonials = [
    {
      quote: "We've automated over 70% of customer queries with AdroitOneAI, freeing our team to focus on complex tasks. Highly recommend!",
      author: "Eline Claes",
      role: "Business Owner"
    },
    {
      quote: "The customization options are fantastic. Our chatbot truly reflects our brand and provides exceptional support to our customers 24/7.",
      author: "Alexander Engel",
      role: "Marketing Director"
    },
    {
      quote: "AdroitOneAI transformed our customer service! The chatbot handles inquiries seamlessly, saving us time and boosting client satisfaction.",
      author: "Sebastian Horn",
      role: "Operations Manager"
    },
    {
      quote: "Their AI chatbot integrated effortlessly into our website and started generating leads within days. A game-changer for our business!",
      author: "Judy Coleman",
      role: "Sales Director"
    }
  ];

  const demoBots = [
    { name: "Restaurant", icon: "🍽️" },
    { name: "RealEstate", icon: "🏠" },
    { name: "E-commerce", icon: "🛒" },
    { name: "Plumbing", icon: "🔧" },
    { name: "Doctor", icon: "👨‍⚕️" },
    { name: "Dentist", icon: "🦷" }
  ];

  const features = [
    {
      icon: "🤖",
      title: "Hybrid AI Chatbots (RAG-powered)",
      description: "Intelligent assistants that answer from your website, PDFs, knowledge base, and APIs—with actions like logging to Sheets, updating CRMs, or sending emails/SMS."
    },
    {
      icon: "🎯",
      title: "Agentic AI & AI Agents",
      description: "Beyond chat—AI that plans, decides, and acts (e.g., scheduling, lead qualification, data entry)."
    },
    {
      icon: "⚡",
      title: "Workflow Automation with n8n",
      description: "Automate repetitive tasks and connect apps (Sheets, Slack, HubSpot, Salesforce, WhatsApp, databases, email services)."
    },
    {
      icon: "🔗",
      title: "Custom Integrations",
      description: "Connect legacy systems, APIs, and cloud platforms for seamless data flow."
    },
    {
      icon: "💻",
      title: "Websites & Web Applications",
      description: "Full design and development of responsive, SEO/AEO-ready websites and robust web apps."
    }
  ];

  const advantages = [
    {
      icon: "🚀",
      title: "Powered by OpenAI",
      description: "Built with the power of Generative AI, your chatbot provides accurate, contextual, and dynamic responses."
    },
    {
      icon: "🎓",
      title: "Custom-Trained Intelligence",
      description: "Reflect your business knowledge and respond based solely on your content."
    },
    {
      icon: "⚡",
      title: "Effortless Integration",
      description: "Seamlessly add one line of code to your website for immediate functionality."
    },
    {
      icon: "🌙",
      title: "24/7 AI-Driven Assistance",
      description: "Works tirelessly to handle inquiries, collect leads, and enhance user experiences."
    }
  ];

  const stats = [
    { number: "200+", label: "Trusted Partner" },
    { number: "100K+", label: "Worldwide User" },
    { number: "10+", label: "Years Of Experience" },
    { number: "24+", label: "Products AI Award" }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-cyan-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Welcome to AdroitOneAI
              </h1>
              <p className="text-2xl md:text-3xl text-blue-100 mb-4 font-semibold">
                AI, Automation & Digital Development
              </p>
              <p className="text-xl text-cyan-100 italic mb-6">
                Intelligently Powerful. Empower. Excel. Limitlessly Scalable.
              </p>
              <p className="text-lg text-blue-100 mb-8 leading-relaxed">
                AdroitOneAI builds AI chatbots, agentic AI systems, workflow automation, and full-stack digital solutions. 
                Using n8n, RAG-powered AI, and custom integrations, we automate processes across industries.
              </p>
              <button className="bg-white text-blue-600 hover:bg-blue-50 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl shadow-lg text-lg">
                Explore Now
              </button>
            </div>
            
            {/* Hero Image Placeholder */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2 h-96 flex items-center justify-center border border-white/20 overflow-hidden">
                <img 
                  src={aiAutomationImg} 
                  alt="AI and Automation Solutions" 
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-400 rounded-full opacity-20 blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-300 rounded-full opacity-20 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What We Do
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI and automation solutions that transform your business operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 group">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our AI Chatbot */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Our AI Chatbot?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full flex items-center justify-center text-white text-2xl mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                  {advantage.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{advantage.title}</h3>
                <p className="text-gray-600 text-sm">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="group">
                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-semibold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demonstration Bots */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Demonstration Bots
            </h2>
            <p className="text-xl text-gray-600">
              Some example bots for you to test…
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {demoBots.map((bot, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 cursor-pointer">
                <div className="text-3xl mb-3">{bot.icon}</div>
                <div className="font-semibold text-gray-900">{bot.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What People Say About AdroitOneAI
            </h2>
            <p className="text-xl text-gray-600">
              Discover how our AI-powered solutions have transformed businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                <p className="text-gray-700 text-lg italic mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-gray-600 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Automate your workflows, engage customers 24/7, and boost efficiency with our AI-powered solutions.
          </p>
          <button className="bg-white text-blue-600 hover:bg-blue-50 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl shadow-lg text-lg">
            Start Your AI Journey
          </button>
        </div>
      </section>
    </div>
  );
};

export default AIAutomation;